import { Controller, useWatch, type Control } from 'react-hook-form';
import { Checkbox } from '@shared/components';
import type { CustomerLinkContacts } from '../../../types/linkDelivery.types';
import { paymentCollectionCopy } from './copy';
import type { DealerCollectedPaymentFormValues } from './dealerCollectedPayment.schema';

interface PaymentLinkDeliveryFieldsProps {
    control: Control<DealerCollectedPaymentFormValues>;
    contacts: CustomerLinkContacts;
}

const EMAIL_HINT_ID = 'payment-link-email-hint';
const SMS_HINT_ID = 'payment-link-sms-hint';

export function PaymentLinkDeliveryFields({ control, contacts }: PaymentLinkDeliveryFieldsProps) {
    const copy = paymentCollectionCopy({ kind: 'link_delivery' });
    const emailTicked = useWatch({ control, name: 'sendViaEmail' }) && contacts.email !== undefined;
    const smsTicked = useWatch({ control, name: 'sendViaSms' }) && contacts.mobile !== undefined;
    const emailLabel = contacts.email ? copy.emailLabel(contacts.email) : copy.emailLabelMissing;
    const smsLabel = contacts.mobile ? copy.smsLabel(contacts.mobile) : copy.smsLabelMissing;

    return (
        <div className="space-y-2" data-testid="payment-link-delivery-fields">
            <div className="flex flex-col gap-1">
                <Controller
                    name="sendViaEmail"
                    control={control}
                    render={({ field }) => (
                        <Checkbox
                            id="payment-link-email"
                            checked={field.value && contacts.email !== undefined}
                            onChange={field.onChange}
                            disabled={contacts.email === undefined}
                            label={emailLabel}
                            ariaLabel={emailLabel}
                            ariaDescribedBy={contacts.email === undefined ? EMAIL_HINT_ID : undefined}
                        />
                    )}
                />
                {contacts.email === undefined && (
                    <p id={EMAIL_HINT_ID} className="pl-7 text-xs text-mp-muted">
                        {copy.noEmailHint}
                    </p>
                )}
            </div>

            <div className="flex flex-col gap-1">
                <Controller
                    name="sendViaSms"
                    control={control}
                    render={({ field }) => (
                        <Checkbox
                            id="payment-link-sms"
                            checked={field.value && contacts.mobile !== undefined}
                            onChange={field.onChange}
                            disabled={contacts.mobile === undefined}
                            label={smsLabel}
                            ariaLabel={smsLabel}
                            ariaDescribedBy={contacts.mobile === undefined ? SMS_HINT_ID : undefined}
                        />
                    )}
                />
                {contacts.mobile === undefined && (
                    <p id={SMS_HINT_ID} className="pl-7 text-xs text-mp-muted">
                        {copy.noMobileHint}
                    </p>
                )}
            </div>

            <div role="status">
                {(emailTicked || smsTicked) && (
                    <p className="text-sm text-mp-foreground">{copy.offSystemNote}</p>
                )}
            </div>
        </div>
    );
}
